import { Fragment, useEffect, useState } from 'react';

import NamingTicket from '../components/Naming';
import NamingComboCard from '../components/NamingComboCard';
import SummaryMobile from '../components/SummaryMobile';
import { EventTrackLabel } from '../constants/ga4';
import { TicketType } from '../constants/tickets';
import { useCartContext } from '../hooks/contexts/cart.context';
import { useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useUiContext } from '../hooks/contexts/ui.context';
import { useUserContext } from '../hooks/contexts/user.context';
import * as Styles from '../styles/pages/naming';
import FacebookPixel from '../trackings/FacebookPixel';
import GoogleAnalytics from '../trackings/GoogleAnalytics';
import { AssignmentTickets, HolderProps } from '../types/cart';
import { associatedFaceRecognitionInThisSession } from '../utils/wallet';

interface EmailData {
  email: string;
  idTicket: number;
}

const NamingPage = () => {
  const { translate } = useI18nContext();
  const { user } = useUserContext();
  const {
    namingData,
    cart,
    updateNamingDataList,
    summaryData,
    combosInCart,
    combosInNaming,
    createComboInNaming,
  } = useCartContext();
  const { isMobile } = useUiContext();
  const { ticketsForThisEventInMyWallet, event, couponApplied } = useEventContext();
  const [emails, setEmails] = useState<EmailData[]>([]);

  const sizeList = namingData.length;

  function remainingAssignment(curNamingData: AssignmentTickets[]): string {
    const total = curNamingData.reduce((acc, item) => {
      return item.holder ? acc : acc + 1;
    }, 0);

    const translationKey = total > 1 ? 'REMAINING_ASSIGNMENTS' : 'REMAINING_ASSIGNMENT';
    return translate(['INFORMATION', translationKey]).replace('<@>', total.toString());
  }

  const addEmail = (data: EmailData) => {
    setEmails((prevEmails) => [...prevEmails, data]);
  };

  const checkDuplicateEmail = (data: EmailData) => {
    return emails.some((item) => item.email === data.email && item.idTicket === data.idTicket);
  };

  const removeEmail = (data: EmailData) => {
    setEmails((prevEmails) =>
      prevEmails.filter(({ email, idTicket }) => !(email === data.email && idTicket === data.idTicket))
    );
  };

  const justOneTicket = namingData.length === 1 && user;

  const handleCheckToMyselfWithBepass = (
    associatedSessionIds: number[],
    curTicket: AssignmentTickets,
    defaultHolder: HolderProps,
  ) => {
    const hasAssociatedFaceInThisSession = associatedFaceRecognitionInThisSession(
      ticketsForThisEventInMyWallet,
      curTicket
    );

    const alreadyAssociatedSession = curTicket.dates.some((curDate) => associatedSessionIds.includes(curDate.id));
    const userHasFaceRegister = user?.faceRecognition.enabled;

    if (alreadyAssociatedSession || !userHasFaceRegister || hasAssociatedFaceInThisSession) {
      return { holder : curTicket.holder, sessionIds : associatedSessionIds };
    }

    const sessionIds = curTicket.dates.map((curDate) => curDate.id);
    return {
      holder : defaultHolder,
      sessionIds : [...associatedSessionIds, ...sessionIds],
    };
  };

  const handlePreAssignTickets = () => {
    let associatedSessionIds: number[] = [];
    const namingDataWithHoldersPreSet = namingData.map((data) => {
      const isntDependent = Number(data.category.id) !== TicketType.DEPENDENT;
      const nonTransferable = !data.isTransferableGuestType && user;
      const fanMember = Number(data.category.id) === TicketType.FAN_MEMBER && user;

      const needsPreAssignRule = (
        isntDependent && (nonTransferable || justOneTicket || fanMember)
      );

      if (needsPreAssignRule) {
        const defaultHolder: HolderProps = {
          id : user.id,
          name : user.name,
          email : user.email,
          picture : user.pictures?.[0].link ?? '',
        };

        if (data.faceRecognition) {
          const { holder, sessionIds } = handleCheckToMyselfWithBepass(associatedSessionIds, data, defaultHolder);
          associatedSessionIds = [...sessionIds];

          return { ...data, holder };
        }

        return { ...data, holder : defaultHolder };
      }
      return data;
    });

    updateNamingDataList(namingDataWithHoldersPreSet);
  };

  const handleAnalyticsTracking = () => {
    GoogleAnalytics.trackEvent(EventTrackLabel.ADD_TO_CART, {
      summaryData,
      event,
      couponApplied,
      currency : String(event?.accepted_currencies),
      value : summaryData.reduce(
        (acc, ticket) => acc + ticket.accumulatedValueInCart,
        0
      ),
      items : summaryData.map((ticket) => ({
        item_id : String(event?.id),
        item_name : String(event?.title),
        coupon : couponApplied ? couponApplied.coupon.name : '',
        item_category : ticket.date,
        item_brand : ticket.group,
        item_variant : ticket.title,
        quantity : ticket.quantity,
        discount : couponApplied ? couponApplied.coupon.value_discount : 0,
        price : ticket.price,
      }))
    });

    const contentsToFbPixelTrack = summaryData.map((ticket) => ({
      id : String(ticket.id),
      quantity : ticket.quantity
    }));

    const valueToFbPixelTrackWithTax = summaryData.reduce((acc, ticket) => (
      acc + (ticket.price + ticket.tax) * ticket.quantity),
    0,
    );

    FacebookPixel.trackAddToCart(
      valueToFbPixelTrackWithTax, contentsToFbPixelTrack, String(event?.accepted_currencies)
    );
  };

  useEffect(() => {
    handlePreAssignTickets();
    createComboInNaming();
    handleAnalyticsTracking();
  }, []);

  const passportsNamingData = namingData.filter((curData) => curData.isPassport);
  const ticketsNamingData = namingData.filter((curData) => !curData.isPassport);
  const startWithRfcModal = (ticket: AssignmentTickets) => (
    justOneTicket &&
    ticket.faceRecognition &&
    !user.faceRecognition.enabled
  );

  if(cart.length === 0 && combosInCart.length === 0) return null;

  return (
    <Styles.WrapperNaming id="naming-page-wrapper">
      {passportsNamingData.length > 0 && (
        <Fragment>
          <Styles.HeaderPage id="naming-page-passports-header">
            <Styles.TitlePage id="naming-page-passports-title">
              {translate(['INFORMATION', 'ASSIGN_PASSPORTS'])}
            </Styles.TitlePage>
            <Styles.RemainingTickets id="naming-page-remaining-passports">
              {remainingAssignment(passportsNamingData)}
            </Styles.RemainingTickets>
          </Styles.HeaderPage>
          <div>
            {passportsNamingData.map((data, index) => (
              <NamingTicket
                isPassport
                key={`${data.id}_passport_naming_${index}`}
                isLastItem={index === sizeList - 1}
                namingData={data}
                startWithRfcModal={startWithRfcModal(data)}
                id={`naming-ticket-${data.id}`}
                addEmail={addEmail}
                checkDuplicateEmail={checkDuplicateEmail}
                removeEmail={removeEmail}
              />
            ))}
          </div>
        </Fragment>
      )}
      {combosInNaming.length > 0 && (
        <Fragment>
          <Styles.HeaderPage id="naming-page-combos-header">
            <Styles.TitlePage id="naming-page-combos-title">
              {translate(['COMBO', 'NAMING_PAGE', 'TITLE'])}
            </Styles.TitlePage>
          </Styles.HeaderPage>
          {combosInNaming.map((combo, index) => (
            <NamingComboCard
              key={`${combo.id}_combo_naming_${index}`}
              namingData={combo}
              startWithRfcModal={false}
              id={`naming-combo-${combo.id}`}
            />
          ))}
        </Fragment>
      )}
      {ticketsNamingData.length > 0 && (
        <Fragment>
          <Styles.HeaderPage id="naming-page-tickets-header">
            <Styles.TitlePage id="naming-page-tickets-title">
              {translate(['INFORMATION', 'ASSIGN_TICKETS'])}
            </Styles.TitlePage>
            <Styles.RemainingTickets id="naming-page-remaining-tickets">
              {remainingAssignment(ticketsNamingData)}
            </Styles.RemainingTickets>
          </Styles.HeaderPage>
          {ticketsNamingData.map((data, index) => (
            <NamingTicket
              isPassport={false}
              key={`${data.id}_ticket_naming_${index}`}
              isLastItem={index === sizeList - 1}
              namingData={data}
              startWithRfcModal={startWithRfcModal(data)}
              id={`naming-ticket-${data.id}`}
              addEmail={addEmail}
              checkDuplicateEmail={checkDuplicateEmail}
              removeEmail={removeEmail}
            />
          ))}
        </Fragment>
      )}
      {isMobile && <SummaryMobile />}
    </Styles.WrapperNaming>
  );
};

export default NamingPage;
